/**
 * TradingView integration helpers — Strategy Builder (Phase 2).
 *
 * Pure, framework-free constants and functions shared by the compact
 * External Signal Mode panel and the TradingView Setup Guide (desktop right
 * panel + mobile bottom sheet). No real tokens live here — callers pass the
 * current strategy token in.
 */

/**
 * The six signal actions, in display order. Labels are plain language; the
 * `code` is what AlgoSys expects in the webhook JSON `type` field.
 */
export const TRADINGVIEW_ACTIONS = [
  { code: 'LE',   label: 'Long Entry' },
  { code: 'LX',   label: 'Exit Long' },
  { code: 'SE',   label: 'Short Entry' },
  { code: 'SX',   label: 'Exit Short' },
  { code: 'LXSE', label: 'Exit Long, then Short' },
  { code: 'SXLE', label: 'Exit Short, then Long' },
];

/** Stable public download path for the sample Pine Script (served from public/). */
export const TRADINGVIEW_SAMPLE_PINE_URL = '/assets/tradingview/AlgoSys_TradingView_Sample.pine';

/**
 * Builds the ready-to-paste TradingView alert message JSON.
 *
 * Assembled as text rather than JSON.stringify of the whole object because
 * `{{close}}` must remain an UNQUOTED TradingView numeric placeholder —
 * TradingView substitutes it when the alert fires, producing valid JSON.
 * `{{ticker}}` stays a quoted string placeholder. The token is JSON-escaped.
 */
export function buildTradingViewAlertJson({ token, action }) {
  return [
    '{',
    `  "strategy_token": ${JSON.stringify(token || '')},`,
    `  "type": ${JSON.stringify(action)},`,
    '  "symbol": "{{ticker}}",',
    '  "price": {{close}},',
    '  "quantity": 1',
    '}',
  ].join('\n');
}

/** TradingView-specific content is shown only for EXTERNAL + TRADINGVIEW. */
export function isTradingViewExternal(strategySource, externalSourceType) {
  return strategySource === 'EXTERNAL' && externalSourceType === 'TRADINGVIEW';
}

/** The Options lots note applies only to Options strategies using By Alert quantity. */
export function showOptionsByAlertLotNote(instrument, quantityMode) {
  return instrument === 'OPTIONS' && quantityMode === 'BY_ALERT';
}

/**
 * Options + By Alert quantity contract:
 * alert quantity = number of lots; broker/exchange quantity = lots × lot size.
 */
export function brokerUnitsForAlertLots(alertLots, lotSize) {
  return alertLots * lotSize;
}

/**
 * Approved worked examples for the Options lots note (contract lot size 65).
 * Rendered by the guide; asserted by tests so the wording cannot drift back
 * to the incorrect "quantity 1 means 65 lots" interpretation.
 */
export const OPTIONS_BY_ALERT_LOT_EXAMPLES = [
  { alertLots: 1,  lotSize: 65, brokerUnits: brokerUnitsForAlertLots(1, 65) },
  { alertLots: 5,  lotSize: 65, brokerUnits: brokerUnitsForAlertLots(5, 65) },
  { alertLots: 65, lotSize: 65, brokerUnits: brokerUnitsForAlertLots(65, 65) },
];
